import React, { useState, useEffect, useCallback } from 'react';
import { _T, C } from '../../shared/theme';
import { getHealthSharingConfig, saveHealthSharingConfig } from './businessApi';

const METRIC_GROUPS = [
  {
    key: 'cognitief',
    icon: '🧠',
    label_nl: 'Cognitief',
    label_en: 'Cognitive',
    metrics: [
      { key: 'concentratie',     icon: '🎯', label_nl: 'Concentratie',      label_en: 'Concentration',    desc_nl: 'Stroop · Focus Tap · Vreemd Symbool',         desc_en: 'Stroop · Focus Tap · Odd Symbol' },
      { key: 'reactievermogen',  icon: '⚡', label_nl: 'Reactievermogen',   label_en: 'Reaction Speed',   desc_nl: 'Gemiddelde reactietijd in ms',                desc_en: 'Average reaction time in ms' },
      { key: 'geheugen',         icon: '🔢', label_nl: 'Geheugen',          label_en: 'Memory',           desc_nl: 'Cijferreeks · Volgorde onthouden',            desc_en: 'Digit span · Sequence recall' },
      { key: 'cognitie',         icon: '💡', label_nl: 'Cognitie',          label_en: 'Cognition',        desc_nl: 'Gecombineerde cognitieve score',              desc_en: 'Combined cognitive score' },
    ],
  },
  {
    key: 'emotioneel',
    icon: '💭',
    label_nl: 'Emotioneel & Zelf',
    label_en: 'Emotional & Self',
    metrics: [
      { key: 'zelfbewustzijn',   icon: '🪞', label_nl: 'Zelfbewustzijn',    label_en: 'Self-awareness',   desc_nl: 'Inzicht in eigen emoties en gedrag',          desc_en: 'Insight into own emotions and behaviour' },
      { key: 'emotiereg',        icon: '🌊', label_nl: 'Emotiereg.',         label_en: 'Emotion Reg.',     desc_nl: 'Vermogen om emoties te reguleren',            desc_en: 'Ability to regulate emotions' },
      { key: 'mentale_helderheid',icon: '☀️',label_nl: 'Mentale Helderheid', label_en: 'Mental Clarity',   desc_nl: 'Focus, energie en helderheid van geest',      desc_en: 'Focus, energy and clarity of mind' },
    ],
  },
  {
    key: 'sociaal',
    icon: '🤝',
    label_nl: 'Sociaal',
    label_en: 'Social',
    metrics: [
      { key: 'soc_aanwezigheid', icon: '👁️', label_nl: 'Soc. Aanwezigheid', label_en: 'Soc. Presence',    desc_nl: 'Betrokkenheid en zichtbaarheid in het team',   desc_en: 'Engagement and visibility in the team' },
      { key: 'empathie',         icon: '💚', label_nl: 'Empathie',          label_en: 'Empathy',          desc_nl: 'Inleven in collega\'s',                       desc_en: 'Relating to colleagues\' feelings' },
      { key: 'expressie',        icon: '🗣️', label_nl: 'Expressie',         label_en: 'Expression',       desc_nl: 'Communicatie en zelfexpressie',               desc_en: 'Communication and self-expression' },
    ],
  },
  {
    key: 'team',
    icon: '👥',
    label_nl: 'Team & Gedrag',
    label_en: 'Team & Behaviour',
    metrics: [
      { key: 'samenwerking',     icon: '🔗', label_nl: 'Samenwerking',      label_en: 'Collaboration',    desc_nl: 'Bijdrage aan teamdoelen en samenwerking',     desc_en: 'Contribution to team goals' },
      { key: 'aanwezigheid',     icon: '📅', label_nl: 'Aanwezigheid',      label_en: 'Attendance',       desc_nl: 'Check-in frequentie en aanwezigheid',         desc_en: 'Check-in frequency and presence' },
      { key: 'leiderschap',      icon: '⭐', label_nl: 'Leiderschap',       label_en: 'Leadership',       desc_nl: 'Initiatief, richting geven en coachen',       desc_en: 'Initiative, direction and coaching' },
      { key: 'consistentie',     icon: '📈', label_nl: 'Consistentie',      label_en: 'Consistency',      desc_nl: 'Stabiliteit in prestaties over tijd',         desc_en: 'Stability in performance over time' },
    ],
  },
  {
    key: 'risico',
    icon: '⚠️',
    label_nl: 'Risico',
    label_en: 'Risk',
    metrics: [
      { key: 'burnout_score',    icon: '🔴', label_nl: 'Burnout Score',     label_en: 'Burnout Score',    desc_nl: 'Gecombineerde burnout-risicoscore (low/medium/high)', desc_en: 'Combined burnout risk score (low/medium/high)' },
    ],
  },
];

const ALL_METRIC_KEYS = METRIC_GROUPS.flatMap(g => g.metrics.map(m => m.key));

const DEFAULT_CONFIG = {
  enabled: false,
  real_time: true,
  metrics: Object.fromEntries(ALL_METRIC_KEYS.map(k => [k, false])),
};

function Toggle({ on, onChange, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!on)}
      style={{
        width: 38, height: 22, borderRadius: 11, flexShrink: 0,
        background: on ? _T.accent : _T.cardBorderBot,
        position: 'relative', cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1, transition: 'background .2s',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3, left: on ? 19 : 3,
        transition: 'left .2s', boxShadow: '0 1px 3px #0004',
      }} />
    </div>
  );
}

export default function BusinessHealthSharingConfig({ lang, businessId, role }) {
  const nl = lang === 'nl';
  const canEdit = role === 'owner' || role === 'admin' || role === 'manager';
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getHealthSharingConfig(businessId)
      .then(d => { if (d && d.metrics) setConfig(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const setMetric = (key, val) =>
    setConfig(c => ({ ...c, metrics: { ...c.metrics, [key]: val } }));

  const setGroupAll = (group, val) =>
    setConfig(c => ({
      ...c,
      metrics: { ...c.metrics, ...Object.fromEntries(group.metrics.map(m => [m.key, val])) },
    }));

  const isGroupAll = (group) => group.metrics.every(m => config.metrics[m.key]);

  const handleSave = async () => {
    setSaving(true);
    await saveHealthSharingConfig(businessId, config).catch(() => {});
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const activeCount = ALL_METRIC_KEYS.filter(k => config.metrics[k]).length;

  const card = { background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '14px 16px', marginBottom: 12 };
  const row  = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 };

  if (loading) return <div style={{ color: C.silver, fontSize: 13, padding: 24, textAlign: 'center' }}>...</div>;

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Header */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ ...row, marginBottom: config.enabled ? 12 : 0 }}>
          <div>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>
              {nl ? '🔗 Gezondheidsdata delen via API' : '🔗 Share Health Data via API'}
            </div>
            <div style={{ color: C.silver, fontSize: 12, marginTop: 3 }}>
              {nl
                ? 'Stel in welke metrieken externe partijen mogen opvragen via de publieke API.'
                : 'Configure which metrics external parties can retrieve via the public API.'}
            </div>
          </div>
          <Toggle on={config.enabled} onChange={v => setConfig(c => ({ ...c, enabled: v }))} disabled={!canEdit} />
        </div>

        {config.enabled && (
          <>
            <div style={{ height: 1, background: _T.cardBorderBot, margin: '10px 0' }} />
            <div style={{ ...row }}>
              <div>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>
                  {nl ? 'Real-time synchronisatie' : 'Real-time sync'}
                </div>
                <div style={{ color: C.silver, fontSize: 11 }}>
                  {nl ? 'Score wijzigingen direct doorsturen naar API-consumers' : 'Push score changes immediately to API consumers'}
                </div>
              </div>
              <Toggle on={config.real_time} onChange={v => setConfig(c => ({ ...c, real_time: v }))} disabled={!canEdit} />
            </div>
          </>
        )}
      </div>

      {/* Metric groups */}
      {config.enabled && (
        <>
          <div style={{ color: C.silver, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
            {nl ? `Metrieken (${activeCount} van ${ALL_METRIC_KEYS.length} actief)` : `Metrics (${activeCount} of ${ALL_METRIC_KEYS.length} active)`}
          </div>

          {METRIC_GROUPS.map(group => (
            <div key={group.key} style={{ ...card }}>
              {/* Group header */}
              <div style={{ ...row, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 17 }}>{group.icon}</span>
                  <span style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>
                    {nl ? group.label_nl : group.label_en}
                  </span>
                </div>
                {canEdit && (
                  <button
                    onClick={() => setGroupAll(group, !isGroupAll(group))}
                    style={{ background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 7, padding: '3px 10px', color: C.silver, fontSize: 11, cursor: 'pointer' }}
                  >
                    {isGroupAll(group)
                      ? (nl ? 'Alle uit' : 'All off')
                      : (nl ? 'Alle aan' : 'All on')}
                  </button>
                )}
              </div>

              {/* Metric rows */}
              {group.metrics.map((m, idx) => (
                <div
                  key={m.key}
                  style={{
                    ...row,
                    padding: '9px 0',
                    borderTop: idx > 0 ? `1px solid ${_T.cardBorderBot}55` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{m.icon}</span>
                    <div>
                      <div style={{ color: config.metrics[m.key] ? C.white : C.silver, fontWeight: 600, fontSize: 13, transition: 'color .2s' }}>
                        {nl ? m.label_nl : m.label_en}
                      </div>
                      <div style={{ color: C.silver2 || C.silver, fontSize: 11, marginTop: 1 }}>
                        {nl ? m.desc_nl : m.desc_en}
                      </div>
                    </div>
                  </div>
                  <Toggle on={!!config.metrics[m.key]} onChange={v => setMetric(m.key, v)} disabled={!canEdit} />
                </div>
              ))}
            </div>
          ))}

          {/* Privacy note */}
          <div style={{ background: C.teal + '12', border: `1px solid ${C.teal}33`, borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: C.teal }}>
            {nl
              ? '🔒 Alleen medewerkers die toestemming hebben gegeven voor datadeling worden meegenomen in de API-respons. Individuele rechten gaan altijd boven deze instelling.'
              : '🔒 Only employees who have granted sharing consent are included in API responses. Individual privacy settings always override this configuration.'}
          </div>

          {/* Save */}
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: saved ? '#4AC87A22' : _T.accent + '22',
                border: `1px solid ${saved ? '#4AC87A55' : _T.accent + '55'}`,
                borderRadius: 10, padding: '10px 28px',
                color: saved ? '#4AC87A' : _T.accent,
                fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saved
                ? (nl ? '✓ Opgeslagen' : '✓ Saved')
                : saving
                  ? (nl ? 'Opslaan…' : 'Saving…')
                  : (nl ? 'Instellingen opslaan' : 'Save settings')}
            </button>
          )}
        </>
      )}
    </div>
  );
}
